import type { SeverityLevel } from '@sentry/remix';

import * as Sentry from '@sentry/remix';

interface SentryTags {
  base: 'Base';
  pricing: 'Pricing';
  analytic: 'Analytic';
  tracking: 'Tracking';
  experiment: 'Experiment';
  shopify: 'Shopify';
  'make-winner': 'MakeWinner';
  app: 'App';
  auth: 'Auth';
  heatmap: 'Heatmap';
}

interface CaptureContext {
  /**
   * Where the error happened.
   * Convention:
   * - Class method: "ClassName.methodName"
   * - Hook: "hookName"
   * - Hook + operation: "hookName.operationName"
   * - Route: "routes.name.loader" or "routes.name.action"
   * - Service function: "serviceName.functionName"
   */
  source: string;
  /** Severity level. Defaults to 'error'. */
  level?: SeverityLevel;
  /** Key-value tags for Sentry filtering (e.g. { pricing: 'Pricing' }). */
  tags: Partial<SentryTags>;
  /** Arbitrary extra data attached to the event. */
  extra?: Record<string, unknown>;
}

/**
 * Capture an error or message to Sentry.
 *
 * - Error instance → Sentry.captureException (preserves stack trace, better grouping)
 * - string → Sentry.captureMessage
 * - Anything else → wraps in Error, attaches original as extra
 */
export function captureSentryError(error: unknown, context: CaptureContext): void {
  try {
    Sentry.withScope((scope) => {
      scope.setLevel(context.level ?? 'error');

      if (context.tags) {
        for (const [key, value] of Object.entries(context.tags)) {
          scope.setTag(key, value);
        }
      }

      scope.setExtra('source', context.source);

      if (context.extra) {
        for (const [key, value] of Object.entries(context.extra)) {
          scope.setExtra(key, value);
        }
      }

      if (error instanceof Error) {
        Sentry.captureException(error);
      } else if (typeof error === 'string') {
        Sentry.captureMessage(error);
      } else {
        const syntheticError = new Error(`[${context.source}] Non-Error exception captured`);
        scope.setExtra('originalError', safeStringify(error));
        Sentry.captureException(syntheticError);
      }
    });
  } catch {
    // Never let Sentry reporting crash the app
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
